import 'intersection-observer';
import React, { Fragment, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { PersistGate } from "redux-persist/integration/react";
import { Provider } from "react-redux";
import App from 'app';
import Loading from "pages/loading";
import reportWebVitals from './reportWebVitals';
import ScrollTop from "components/scrollTop";
import store, { persistor } from 'store';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

// Temporarily suppress defaultProps warnings from reactstrap until we can upgrade
const originalError = console.error;
console.error = (...args: any[]) => {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('Support for defaultProps will be removed') ||
      args[0].includes('Badge: Support for defaultProps'))
  ) {
    return; // Suppress these specific warnings
  }
  originalError.call(console, ...args);
};

const BUGSNAG_ID: string | undefined = process.env.REACT_APP_BUGSNAG_ID;
const BUGSNAG_ENV: string | undefined = process.env.REACT_APP_BUGSNAG_ENV;
const bugsnagEnabled: boolean = BUGSNAG_ID !== undefined && BUGSNAG_ENV !== undefined;

if (bugsnagEnabled) {
  Bugsnag.start({
    onError: event =>
      new Promise((resolve, reject) => {
        //exclude unhandledrejection error 
        if (event.errors[0].errorClass === "InvalidError" && event.context === "/login") {
          resolve(false);
        }
        else {
          resolve(true);
        }
      }),
    apiKey: BUGSNAG_ID!,
    releaseStage: BUGSNAG_ENV,
    appVersion: process.env.REACT_APP_VERSION,
    plugins: [new BugsnagPluginReact()],
  });
}

const ErrorBoundary = bugsnagEnabled ?
  Bugsnag.getPlugin('react')!.createErrorBoundary(React) :
  Fragment;

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);
root.render(
  <StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <PersistGate
          loading={<Loading />}
          persistor={persistor}
        >
          <ScrollTop>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </ScrollTop>
        </PersistGate>
      </BrowserRouter>
      <ToastContainer
        autoClose={3000}
        className="toast-alert"
        position="top-center"
        hideProgressBar={true}
      />
    </Provider>
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
